import React, { useEffect, useState } from 'react';
import { Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import { savePendingAction } from '../../utils/pendingAction';
import { useCachedImageUrl } from '../../hooks/useCachedImageUrl';
import './ProductCard.css';
import './ComboCard.css';

const ComboCard = ({ combo, buttonText = 'View Combo' }) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [inWishlist, setInWishlist] = useState(combo.in_wishlist === true);

  const price = parseFloat(combo.price) || 0;
  const strikePrice = parseFloat(combo.strike_through_price) || 0;

  const imageUrl = combo.thumbnail_image || combo.products?.[0]?.primary_image || '/placeholder.png';
  const { src: cachedImageSrc, loading: imageLoading } = useCachedImageUrl(imageUrl);
  const displayImageSrc = imageLoading ? '/placeholder.png' : cachedImageSrc || imageUrl || '/placeholder.png';

  useEffect(() => {
    if (combo.in_wishlist === true || combo.in_wishlist === false) {
      setInWishlist(combo.in_wishlist === true);
    }
  }, [combo.in_wishlist]);

  const handleCardClick = (e) => {
    if (e.target.closest('button') || e.target.closest('a') || e.target.closest('.product-wishlist-btn')) {
      return;
    }
    navigate(`/combos/${combo.id}`);
  };

  const handleViewCombo = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/combos/${combo.id}`);
  };

  const handleWishlistToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      savePendingAction({ type: 'addToWishlist', comboId: combo.id });
      toast.info('Please log in to use the wishlist');
      setTimeout(() => navigate('/login'), 400);
      return;
    }

    try {
      if (inWishlist) {
        await api.delete(`/wishlist/combo/${combo.id}`);
        setInWishlist(false);
        toast.success('Removed from wishlist');
        window.dispatchEvent(new Event('wishlistUpdated'));
      } else {
        await api.post('/wishlist', { combo_id: combo.id });
        setInWishlist(true);
        toast.success('Added to wishlist');
        window.dispatchEvent(new Event('wishlistUpdated'));
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to update wishlist';
      if (errorMessage.toLowerCase().includes('already in wishlist')) {
        setInWishlist(true);
      } else {
        toast.error(errorMessage);
      }
    }
  };

  return (
    <Card className="product-card combo-card" onClick={handleCardClick}>
      <div className={`product-image-wrapper ${imageLoading ? 'is-loading' : ''}`}>
        {imageLoading ? (
          <div className="product-image-skeleton" aria-hidden="true" />
        ) : (
          <img src={displayImageSrc} alt={combo.title || 'Combo'} />
        )}

        {/* Badge priority: Combo > Sale > New */}
        <div className="product-badges">
          <span className="product-badge product-badge-combo">Combo</span>
        </div>

        <button
          type="button"
          className={`product-wishlist-btn ${inWishlist ? 'in-wishlist' : ''}`}
          onClick={handleWishlistToggle}
          title={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
          aria-label={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill={inWishlist ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
        </button>
      </div>

      <Card.Body>
        <h5 className="product-name">{combo.title}</h5>
        {combo.description && (
          <p className="product-category combo-card-description">{combo.description}</p>
        )}

        <div className="product-price-section">
          <div className="product-price">
            <span className="current-price">₹{price.toLocaleString('en-IN')}</span>
            {strikePrice > price && (
              <span className="original-price">
                ₹{strikePrice.toLocaleString('en-IN')}
              </span>
            )}
          </div>
        </div>

        <button type="button" className="view-product-btn" onClick={handleViewCombo}>
          {buttonText}
        </button>
      </Card.Body>
    </Card>
  );
};

export default ComboCard;
